import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowUpRight, Globe2, Mail, MapPin, Phone } from "lucide-react";
import { useSiteData } from "../siteData";
import { useI18n } from "../i18n";

const STUDIOS = [
  {
    id: "istanbul",
    flag: "TR",
    label: "İstanbul, Turkiye",
    name: "HRD Mimarlık",
    coords: [41.06067, 28.97024],
    coordLabel: "[ 41.0607° N, 28.9702° E ]",
    zoom: 16,
    lines: [
      "Perpa Ticaret Merkezi B Blok",
      "Kat: 11, No: 1719",
      "Okmeydanı, İstanbul / Turkiye",
    ],
    phones: ["+90 212 220 76 16", "+90 212 220 76 86"],
    email: "info@hrdmimarlik.com",
    note: "Production, joinery & site supervision",
  },
  {
    id: "mantsala",
    flag: "FI",
    label: "Helsinki, Finland",
    name: "HRD Architecture / Keiki Concept Oy",
    coords: [60.62667, 25.29533],
    coordLabel: "[ 60.6267° N, 25.2953° E ]",
    zoom: 16,
    lines: ["Maisalantie 2 b", "04600 Mäntsälä", "Finland"],
    phones: ["+358 41 3103702"],
    phoneNote: "WhatsApp",
    email: "info@hrdarchitecture.com",
    note: "Design studio & MATCH collection HQ · WhatsApp available",
  },
];

const OVERVIEW = { center: [53.5, 28.0], zoom: 4 };
// south of Türkiye  ->  north of Finland (keeps Finland fully in frame)
const TERRITORY_BOUNDS = [
  [35.4, 18.5],
  [70.6, 45.5],
];

// company labels rendered directly on the map canvas
const MAP_LABELS = [
  {
    id: "fi-label",
    position: [63.4, 36.0],
    title: "HRD ARCHITECTURE",
    sub: "KEIKI CONCEPT OY · HELSINKI",
  },
  {
    id: "tr-label",
    position: [44.6, 33.8],
    title: "HRD MİMARLIK",
    sub: "OKMEYDANI · İSTANBUL",
  },
];

const labelIcon = (l) =>
  L.divIcon({
    className: "hrd-map-label",
    html: `<div style="
        display:inline-block;white-space:nowrap;transform:translate(-50%,-50%);
        background:#141414;padding:7px 12px;
        box-shadow:0 4px 16px rgba(20,20,20,0.35);">
        <div style="font-family:'Archivo',sans-serif;font-weight:700;letter-spacing:0.01em;
          font-size:12.5px;line-height:1;color:#FFFFFF;text-transform:uppercase;">${l.title}</div>
        <div style="font-family:'JetBrains Mono',monospace;font-size:8px;letter-spacing:0.12em;
          color:#D4C3AC;margin-top:5px;line-height:1;">${l.sub}</div>
      </div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

const pinIcon = L.divIcon({
  className: "hrd-pin",
  html: `<span style="display:block;width:14px;height:14px;background:#141414;border:3px solid #FAFAFA;box-shadow:0 2px 10px rgba(20,20,20,0.32);"></span>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -12],
});

// country fills are only meaningful at territory scale — hide them when
// the camera is flown down to a street-level view
function CountryLayers({ geo, style }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on("zoomend", onZoom);
    return () => map.off("zoomend", onZoom);
  }, [map]);

  if (zoom > 7) return null;

  return (
    <>
      {geo.TUR && <GeoJSON key="tur" data={geo.TUR} style={() => style} />}
      {geo.FIN && <GeoJSON key="fin" data={geo.FIN} style={() => style} />}
    </>
  );
}

function FlyController({ target, markerRefs }) {
  const map = useMap();
  const mounted = useRef(false);

  useEffect(() => {
    if (!map) return;

    if (!mounted.current) {
      mounted.current = true;
      if (target) {
        map.setView(target.coords, target.zoom);
        const t0 = setTimeout(() => {
          const m0 = markerRefs.current[target.id];
          if (m0) m0.openPopup();
        }, 400);
        return () => clearTimeout(t0);
      }
      // make sure the whole production territory (incl. northern Finland) fits
      map.fitBounds(TERRITORY_BOUNDS, { padding: [24, 24] });
      return;
    }

    if (target) {
      map.flyTo(target.coords, target.zoom, { duration: 2.2, easeLinearity: 0.24 });
      const t = setTimeout(() => {
        const m = markerRefs.current[target.id];
        if (m) m.openPopup();
      }, 2350);
      return () => clearTimeout(t);
    }

    map.closePopup();
    map.flyToBounds(TERRITORY_BOUNDS, { padding: [24, 24], duration: 1.9 });
  }, [target, map, markerRefs]);

  return null;
}

const directionsUrl = (s) =>
  s.mapAddress
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.mapAddress)}`
    : `https://www.google.com/maps/dir/?api=1&destination=${s.coords[0]},${s.coords[1]}`;

function AddressCard({ studio, wide = false }) {
  return (
    <div
      className={`bg-white/70 backdrop-blur-md border border-[#E2E0D8] p-6 lg:p-8 ${
        wide ? "" : "h-full"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="mono text-[9px] text-[#8C7862]">
          [ {studio.flag} ] {studio.label.toUpperCase()}
        </p>
        <span className="w-2 h-2 rounded-full bg-[#C8B29B] shrink-0 mt-1" />
      </div>

      <h3 className="display text-[24px] lg:text-[30px] text-[#141414] mt-5 leading-[1.04]">
        {studio.name}
      </h3>

      <div className="mt-6 pt-5 border-t border-stone-200 space-y-1">
        {studio.lines.map((l) => (
          <p key={l} className="text-[14.5px] text-[#141414]/75 leading-relaxed">
            {l}
          </p>
        ))}
      </div>

      <div className="mt-5 pt-5 border-t border-stone-200 space-y-2.5">
        {studio.phones.map((p) => (
          <a
            key={p}
            href={`tel:${p.replace(/\s/g, "")}`}
            className="flex items-center gap-3 text-[14px] text-[#141414]/80 hover:text-[#141414] transition-colors"
          >
            <Phone size={13} strokeWidth={1.6} className="text-[#C8B29B] shrink-0" />
            <span>{p}</span>
            {studio.phoneNote && (
              <span className="mono text-[8px] text-[#8C7862] border border-[#E2E0D8] px-1.5 py-0.5">
                {studio.phoneNote}
              </span>
            )}
          </a>
        ))}
        <a
          href={`mailto:${studio.email}`}
          className="flex items-center gap-3 text-[14px] text-[#141414] transition-colors"
        >
          <Mail size={13} strokeWidth={1.6} className="text-[#C8B29B] shrink-0" />
          <span className="link-underline">{studio.email}</span>
        </a>
      </div>

      <div className="mt-6 pt-5 border-t border-stone-200 flex flex-wrap items-center justify-between gap-4">
        <p className="mono text-[9px] text-[#6B675F] tracking-[0.14em]">{studio.coordLabel}</p>
        <a
          href={directionsUrl(studio)}
          target="_blank"
          rel="noopener noreferrer"
          className="mono text-[9px] text-[#141414] inline-flex items-center gap-1.5 link-underline"
        >
          [ GET DIRECTIONS <ArrowUpRight size={11} strokeWidth={1.9} /> ]
        </a>
      </div>

      <p className="mono text-[8.5px] text-[#A8A29A] mt-5">{studio.note}</p>
    </div>
  );
}

export default function StudioMap({ initialId = "overview" }) {
  const [activeId, setActiveId] = useState(initialId);
  const [tilesFailed, setTilesFailed] = useState(false);
  const markerRefs = useRef({});
  const [geo, setGeo] = useState({ TUR: null, FIN: null });

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch("/geo/TUR.geo.json").then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch("/geo/FIN.geo.json").then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([TUR, FIN]) => {
      if (alive) setGeo({ TUR, FIN });
    });
    return () => {
      alive = false;
    };
  }, []);

  const { content } = useSiteData();
  const { lang } = useI18n();
  const offices = useMemo(() => content.contact?.offices || [], [content]);

  // office data (public city label, exact map coordinates and directions address)
  // is managed from the admin panel; STUDIOS only provides the fallback.
  const studios = useMemo(
    () =>
      STUDIOS.map((s) => {
        const o = offices.find((x) => (x.mapId || "") === s.id);
        if (!o) return s;
        const lat = parseFloat(o.lat);
        const lng = parseFloat(o.lng);
        const coords = Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : s.coords;
        const pick = (v, fb) => (v && (v[lang] || v.en)) || fb;
        return {
          ...s,
          label: pick(o.city, s.label),
          name: o.name || s.name,
          coords,
          coordLabel: `[ ${coords[0].toFixed(4)}° N, ${coords[1].toFixed(4)}° E ]`,
          lines: o.address && o.address.length ? o.address : s.lines,
          phones: o.phones && o.phones.length ? o.phones : s.phones,
          phoneNote: o.phoneNote || s.phoneNote,
          email: o.email || s.email,
          note: pick(o.mapNote, s.note),
          mapAddress: o.mapAddress || "",
        };
      }),
    [offices, lang]
  );

  const active = useMemo(() => studios.find((s) => s.id === activeId) || null, [activeId, studios]);

  const countryStyle = {
    fillColor: "#D4C3AC",
    fillOpacity: 0.55,
    color: "#A9855F",
    weight: 1.2,
  };

  const labelOf = (id) => (studios.find((s) => s.id === id)?.label || id).toUpperCase();
  const buttons = [
    { id: "istanbul", tag: "[ TR ]", text: labelOf("istanbul"), Icon: MapPin },
    { id: "overview", tag: "[ EU ]", text: "ALL STUDIOS / OVERVIEW", Icon: Globe2 },
    { id: "mantsala", tag: "[ FI ]", text: labelOf("mantsala"), Icon: MapPin },
  ];

  return (
    <div>
      {/* ---------- map canvas ---------- */}
      <div className="relative isolate z-0 border border-[#E2E0D8] bg-[#FAFAFA] overflow-hidden">
        <div className="h-[360px] sm:h-[440px] lg:h-[540px] w-full">
          <MapContainer
            center={OVERVIEW.center}
            zoom={OVERVIEW.zoom}
            minZoom={3}
            scrollWheelZoom={false}
            zoomControl={false}
            attributionControl={false}
            style={{ height: "100%", width: "100%", background: "#F5F5F3" }}
          >
            <ZoomControl position="bottomright" />
            {tilesFailed ? (
              <TileLayer
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
            ) : (
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                subdomains={["a", "b", "c", "d"]}
                maxZoom={19}
                eventHandlers={{ tileerror: () => setTilesFailed(true) }}
              />
            )}

            <CountryLayers geo={geo} style={countryStyle} />

            {studios.map((s) => (
              <Marker
                key={s.id}
                position={s.coords}
                icon={pinIcon}
                ref={(el) => {
                  if (el) markerRefs.current[s.id] = el;
                }}
              >
                <Popup>
                  <span className="block font-medium text-[13px] text-[#141414]">{s.name}</span>
                  <span className="block text-[12px] text-[#141414]/70 mt-1">
                    {s.lines.join(", ")}
                  </span>
                  <span className="block text-[12px] text-[#141414] mt-1">{s.email}</span>
                </Popup>
              </Marker>
            ))}

            {MAP_LABELS.map((l) => (
              <Marker
                key={l.id}
                position={l.position}
                icon={labelIcon(l)}
                interactive={false}
                keyboard={false}
              />
            ))}

            <FlyController target={active} markerRefs={markerRefs} />
          </MapContainer>
        </div>

        {/* legend */}
        <div className="absolute top-4 left-4 z-[500] bg-[#FAFAFA]/92 backdrop-blur-sm border border-[#E2E0D8] px-3.5 py-3">
          <p className="mono text-[8.5px] text-[#6B675F] mb-2">[ PRODUCTION TERRITORY ]</p>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 border border-[#A9855F] bg-[#D4C3AC]" />
            <span className="mono text-[8.5px] text-[#141414]">Türkiye &amp; Finland</span>
          </div>
        </div>
      </div>

      {/* ---------- 3-button control bar ---------- */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#E2E0D8] border border-stone-300">
        {buttons.map((b) => {
          const isActive = activeId === b.id;
          const Icon = b.Icon;
          return (
            <button
              key={b.id}
              onClick={() => setActiveId(b.id)}
              className={`group flex items-center justify-between gap-3 px-5 py-4 cursor-pointer transition-colors duration-300 ${
                isActive ? "bg-[#141414]" : "bg-[#FAFAFA] hover:bg-stone-200"
              }`}
            >
              <span className="flex items-center gap-3 min-w-0">
                <span
                  className={`w-2 h-2 rounded-full shrink-0 transition-colors duration-300 ${
                    isActive ? "bg-[#C8B29B]" : "bg-stone-400 group-hover:bg-[#A9855F]"
                  }`}
                />
                <span
                  className={`mono text-[9px] truncate ${
                    isActive ? "text-[#FAFAFA]" : "text-[#141414]"
                  }`}
                >
                  {b.tag} {b.text}
                </span>
              </span>
              <Icon
                size={13}
                strokeWidth={1.6}
                className={`shrink-0 transition-colors duration-300 ${
                  isActive ? "text-[#C8B29B]" : "text-[#141414]/40 group-hover:text-[#141414]"
                }`}
              />
            </button>
          );
        })}
      </div>

      {/* ---------- editorial info drawer ---------- */}
      <div className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-stone-200">
          <p className="mono text-[9px] text-[#6B675F] tracking-[0.16em]">
            {active
              ? `FLY-TO · ${active.label.toUpperCase()} · ZOOM ${active.zoom}`
              : "TERRITORY OVERVIEW · TÜRKİYE & FINLAND"}
          </p>
          <p className="mono text-[9px] text-[#141414] tracking-[0.16em]">
            {active ? active.coordLabel : "[ 51.5000° N, 27.5000° E ]"}
          </p>
        </div>

        <div className={`mt-6 grid gap-6 ${active ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}>
          {(active ? [active] : studios).map((s) => (
            <AddressCard key={s.id} studio={s} wide={!!active} />
          ))}
        </div>

        <p className="mono text-[8.5px] text-[#A8A29A] mt-6">
          Studio hours: Mon to Fri, 09:00 - 18:00 (EET / EEST)
        </p>
      </div>
    </div>
  );
}
